import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';

function useInView(ref: React.RefObject<HTMLElement | null>) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setInView(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return inView;
}

const PROJECTS = [
  {
    title: 'Global Port Authority — RFID Asset Management',
    category: 'RFID Solution',
    tags: ['RFID', 'Asset Tracking', 'Real-time'],
    desc: 'Deployed a full-scale RFID asset management solution across 6 port terminals. Real-time tracking of 50,000+ assets reduced loss incidents by 94%.',
    img: 'https://readdy.ai/api/search-image?query=port%20terminal%20logistics%20management%20software%20dashboard%20RFID%20asset%20tracking%20maritime%20harbor%20operations%20real-time%20map%20visualization%20modern%20UI%20teal%20accent%20professional&width=600&height=400&seq=proj-port&orientation=landscape',
    results: ['94% reduction in asset loss', '50,000+ assets tracked', '6 port terminals'],
  },
  {
    title: 'National University Network — Library Management',
    category: 'RFID Library System',
    tags: ['RFID', 'Library', 'Education'],
    desc: 'Implemented RFID library management system across 12 campus libraries. Self-checkout kiosks reduced staff workload by 70% while improving patron experience.',
    img: 'https://readdy.ai/api/search-image?query=university%20library%20RFID%20self%20checkout%20kiosk%20system%20digital%20catalog%20management%20modern%20library%20technology%20clean%20bright%20interior%20education%20institution&width=600&height=400&seq=proj-lib&orientation=landscape',
    results: ['70% staff workload reduction', '12 campuses deployed', '200K+ book inventory'],
  },
  {
    title: 'Multi-Chain Retail Group — POS & Inventory',
    category: 'POS + Inventory',
    tags: ['POS', 'Inventory', 'Retail'],
    desc: 'Custom POS system with integrated inventory management deployed across 85 retail outlets. Real-time inventory sync reduced stockouts by 87%.',
    img: 'https://readdy.ai/api/search-image?query=retail%20chain%20POS%20point%20of%20sale%20system%20with%20inventory%20management%20dashboard%20multiple%20store%20locations%20real-time%20sync%20modern%20touchscreen%20interface%20clean%20white%20background%20professional&width=600&height=400&seq=proj-retail&orientation=landscape',
    results: ['87% reduction in stockouts', '85 retail outlets', 'Real-time sync'],
  },
  {
    title: 'Maritime Supply Co. — Ship Chandling Platform',
    category: 'Maritime Software',
    tags: ['Ship Chandling', 'Maritime', 'Quotation'],
    desc: 'End-to-end ship chandling quotation and procurement platform. Automated supplier comparison cut quotation turnaround from 48 hours to under 2 hours.',
    img: 'https://readdy.ai/api/search-image?query=ship%20chandling%20maritime%20supply%20management%20software%20dashboard%20quotation%20system%20port%20operations%20procurement%20platform%20clean%20modern%20UI%20professional%20teal%20accents%20white%20background&width=600&height=400&seq=proj-maritime&orientation=landscape',
    results: ['96% faster quotations', '300+ suppliers integrated', '$2M annual savings'],
  },
  {
    title: 'Healthcare Group — AI Patient Analytics',
    category: 'AI Solution',
    tags: ['AI', 'Healthcare', 'Analytics'],
    desc: 'AI-powered patient flow prediction and analytics dashboard for a 5-hospital healthcare group. Reduced average wait times by 40% through intelligent scheduling.',
    img: 'https://readdy.ai/api/search-image?query=healthcare%20hospital%20AI%20analytics%20dashboard%20patient%20flow%20prediction%20intelligent%20scheduling%20medical%20data%20visualization%20modern%20clean%20UI%20professional%20teal%20white%20background&width=600&height=400&seq=proj-health&orientation=landscape',
    results: ['40% reduction in wait times', '5 hospitals', '99.2% prediction accuracy'],
  },
  {
    title: 'Publishing House — Magic Photo Press Suite',
    category: 'Publishing Software',
    tags: ['Photo Press', 'Publishing', 'Ebook'],
    desc: 'Enterprise licensing of Magic Photo Press for a leading publishing house. Streamlined their entire photo editing and ebook production workflow into a single platform.',
    img: 'https://readdy.ai/api/search-image?query=digital%20publishing%20house%20photo%20editing%20ebook%20production%20workflow%20software%20interface%20professional%20creative%20studio%20team%20working%20multiple%20screens%20modern%20bright%20office&width=600&height=400&seq=proj-publish&orientation=landscape',
    results: ['60% faster production', '2,000+ ebooks published', 'Team of 50 editors'],
  },
];

const FILTERS = ['All', 'AI Solution', 'RFID Solution', 'POS + Inventory', 'Maritime Software', 'RFID Library System', 'Publishing Software'];

export default function PortfolioPage() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref);
  const [active, setActive] = useState('All');

  const filtered = active === 'All' ? PROJECTS : PROJECTS.filter((p) => p.category === active);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-white overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-50/80 blur-3xl -translate-y-1/4 translate-x-1/4" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10 text-center">
            {/* <span className="inline-block bg-teal-100 text-teal-700 text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-5">
              Portfolio
            </span> */}
            <h1 className="text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight mb-5">
              Our <span className="text-teal-500">Work</span> Speaks
            </h1>
            <p className="text-gray-500 text-xl max-w-2xl mx-auto">
              500+ successful projects delivered over 25 years. Here are some of our featured case studies.
            </p>
          </div>
        </section>

        {/* Filters & Projects */}
        <section ref={ref} className="pb-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            {/* Filter Bar */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setActive(f)}
                  className={`cursor-pointer whitespace-nowrap text-sm font-medium px-5 py-2 rounded-full transition-all ${
                    active === f
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-700'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>

            {/* Project Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((proj, i) => (
                <div
                  key={proj.title}
                  className={`group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-teal-200 transition-all duration-500 ${
                    inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="w-full h-48 overflow-hidden">
                    <img
                      src={proj.img}
                      alt={proj.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {proj.tags.map((t) => (
                        <span key={t} className="text-xs bg-teal-50 text-teal-700 border border-teal-200 px-2.5 py-1 rounded-full">{t}</span>
                      ))}
                    </div>
                    <h3 className="text-gray-900 font-bold mb-2 text-base leading-snug">{proj.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-5">{proj.desc}</p>
                    <div className="flex flex-col gap-1.5 pt-4 border-t border-gray-100">
                      {proj.results.map((r) => (
                        <div key={r} className="flex items-center gap-2 text-xs text-gray-600">
                          <i className="ri-check-double-line text-teal-500" /> {r}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-teal-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-extrabold text-white mb-4">Ready to Start Your Project?</h2>
            <p className="text-teal-100 text-base mb-8">Join 200+ satisfied clients who trust us to deliver exceptional IT solutions.</p>
            <Link to="/contact" className="cursor-pointer whitespace-nowrap bg-white hover:bg-gray-50 text-teal-700 font-semibold px-8 py-4 rounded-xl transition-colors inline-flex items-center gap-2">
              <i className="ri-calendar-check-line" /> Get Free Consultation
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
